/**
 * Estado de uma conversa do Assistente no navegador: histórico carregado por
 * tRPC + o turno em andamento vindo do SSE.
 *
 * Enquanto um turno roda, a lista é mantida localmente (texto chegando,
 * tools em andamento). No `fim`, o histórico é recarregado do servidor e
 * passa a ser a verdade — os ids temporários somem.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { trpc } from "@/lib/trpc";
import { lerEventosSse } from "@/lib/assistente-sse";
import {
  ASSISTENTE_ACAO_PATH,
  ASSISTENTE_STREAM_PATH,
  type AssistenteAcao,
  type AssistenteEvento,
  type AssistentePendencia,
  type AssistenteStreamPedido,
} from "@shared/assistente-contracts";
import { caminhoPermitido } from "@shared/assistente-telas";
import { executarAcao, lerContexto } from "./contexto-de-tela";

export type FerramentaUi = { id: string; nome: string; titulo?: string; ok?: boolean; resumo?: string; emAndamento?: boolean };
export type AnexoUi = {
  id: number;
  fileName: string;
  kind: string;
  size: number;
  /** Miniatura local (URL de objeto) enquanto a mensagem não foi enviada. */
  previewUrl?: string;
};
/** O que está acontecendo agora, para a tela não parecer travada. */
export type EtapaUi = { texto: string; desde: number } | null;

export type MensagemUi = {
  id: number | string;
  role: "user" | "assistant";
  content: string;
  anexos?: AnexoUi[];
  ferramentas: FerramentaUi[];
  acoes?: AssistenteAcao[] | null;
  pendencia?: AssistentePendencia | null;
  streaming?: boolean;
  /** Resposta montada no navegador (plano sem o Assistente): mostra a vitrine. */
  upsell?: boolean;
};

const CHAVE_MODULO = "assistente:modulo";

export function useAssistente(opts: {
  conversationId: number | null;
  onConversaCriada?: (id: number) => void;
  onNavegou?: () => void;
  /** A conversa lembrada não existe mais (excluída em outra aba): quem chama volta para "nova". */
  onConversaInvalida?: () => void;
}) {
  const { conversationId, onConversaCriada, onNavegou, onConversaInvalida } = opts;
  const utils = trpc.useUtils();
  const [location, setLocation] = useLocation();
  const [mensagens, setMensagens] = useState<MensagemUi[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [etapa, setEtapa] = useState<EtapaUi>(null);
  // Área escolhida no botão "+". Lembrada entre sessões: quem trabalha o dia
  // no WhatsApp não quer reescolher a cada visita.
  const [modulo, setModuloEstado] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CHAVE_MODULO);
    } catch {
      return null;
    }
  });
  const moduloRef = useRef<string | null>(modulo);
  const setModulo = useCallback((id: string | null) => {
    moduloRef.current = id;
    setModuloEstado(id);
    try {
      if (id) localStorage.setItem(CHAVE_MODULO, id);
      else localStorage.removeItem(CHAVE_MODULO);
    } catch {
      /* armazenamento indisponível: vale só nesta sessão */
    }
  }, []);
  const abortRef = useRef<AbortController | null>(null);
  const ocupadoRef = useRef(false);
  const conversaAtualRef = useRef<number | null>(conversationId);
  /** Conversa do turno em andamento (conhecida no `inicio` quando é nova). */
  const turnoConversaRef = useRef<number | null>(null);

  // Quem hospeda o hook sumiu (página trocou): encerra o turno em vez de
  // deixar o fetch e o loop do servidor rodando sem ninguém ouvindo.
  useEffect(() => () => abortRef.current?.abort(), []);

  const historico = trpc.assistente.mensagens.useQuery(
    { conversationId: conversationId ?? 0 },
    { enabled: conversationId !== null, staleTime: 5_000, retry: false },
  );

  useEffect(() => {
    if (historico.error?.data?.code === "NOT_FOUND") onConversaInvalida?.();
  }, [historico.error, onConversaInvalida]);

  // Histórico do servidor vira a lista quando não há turno rodando. Trocar de
  // conversa (ou "Nova conversa") com um turno em andamento aborta o turno:
  // senão a lista da conversa antiga ficava na tela sob a nova e o próximo
  // envio ia para a conversa errada.
  useEffect(() => {
    if (ocupadoRef.current && turnoConversaRef.current !== conversationId) {
      abortRef.current?.abort();
      ocupadoRef.current = false;
      setOcupado(false);
    }
    conversaAtualRef.current = conversationId;
    if (conversationId === null) {
      if (!ocupadoRef.current) setMensagens([]);
      return;
    }
    if (ocupadoRef.current || !historico.data) return;
    setMensagens(
      historico.data.mensagens.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        anexos: (m.anexos ?? []) as AnexoUi[],
        ferramentas: m.ferramentas.map((f) => ({ id: f.id, nome: f.nome, ok: f.ok, resumo: f.resumo })),
        acoes: (m.acoes as AssistenteAcao[] | null) ?? null,
        pendencia: (m.pendencia as AssistentePendencia | null) ?? null,
      })),
    );
  }, [conversationId, historico.data]);

  const atualizarAssistente = useCallback((fn: (m: MensagemUi) => MensagemUi) => {
    setMensagens((lista) => {
      const i = lista.length - 1;
      if (i < 0 || lista[i].role !== "assistant") return lista;
      const copia = lista.slice();
      copia[i] = fn(copia[i]);
      return copia;
    });
  }, []);

  const rodar = useCallback(
    async (pedido: AssistenteStreamPedido) => {
      if (ocupadoRef.current) return;
      ocupadoRef.current = true;
      setOcupado(true);
      setEtapa({ texto: "Conectando", desde: Date.now() });
      const controller = new AbortController();
      abortRef.current = controller;
      let conversaDoTurno = pedido.conversationId ?? null;
      turnoConversaRef.current = conversaDoTurno;
      let navegarPara: string | null = null;

      const tratar = (ev: AssistenteEvento) => {
        switch (ev.tipo) {
          case "inicio":
            conversaDoTurno = ev.conversationId;
            turnoConversaRef.current = ev.conversationId;
            if (pedido.conversationId == null) {
              conversaAtualRef.current = ev.conversationId;
              onConversaCriada?.(ev.conversationId);
            }
            break;
          case "etapa":
            setEtapa(ev.texto ? { texto: ev.texto, desde: Date.now() } : null);
            break;
          case "texto":
            atualizarAssistente((m) => ({ ...m, content: m.content + ev.delta }));
            break;
          case "ferramenta_inicio":
            atualizarAssistente((m) => ({ ...m, ferramentas: [...m.ferramentas, { id: ev.id, nome: ev.nome, titulo: ev.titulo, emAndamento: true }] }));
            break;
          case "ferramenta_fim":
            atualizarAssistente((m) => ({
              ...m,
              ferramentas: m.ferramentas.some((f) => f.id === ev.id)
                ? m.ferramentas.map((f) => (f.id === ev.id ? { ...f, ok: ev.ok, resumo: ev.resumo, emAndamento: false } : f))
                : [...m.ferramentas, { id: ev.id, nome: "", ok: ev.ok, resumo: ev.resumo }],
            }));
            break;
          case "acoes":
            atualizarAssistente((m) => ({ ...m, acoes: [...(m.acoes ?? []), ...ev.acoes].slice(0, 6) }));
            break;
          case "acao_tela":
            // A ação mexe no que está aberto (campo de resposta, assunto) e o
            // resultado REAL volta ao turno: sem isso o assistente afirmaria
            // ter escrito numa tela que a pessoa já trocou.
            void executarAcao(ev.acaoId, ev.args).then((r) =>
              fetch(ASSISTENTE_ACAO_PATH, {
                method: "POST",
                headers: { "content-type": "application/json" },
                credentials: "same-origin",
                body: JSON.stringify({ toolCallId: ev.id, conversationId: conversaDoTurno, ok: r.ok, detalhe: r.detalhe }),
              }).catch(() => {
                /* o turno cai no tempo limite e avisa que não confirmou */
              }),
            );
            break;
          case "dados":
            // Invalida tudo o que está ativo: o React Query só refaz o que a
            // tela realmente usa, e acertar o escopo exato exigiria um mapa
            // de tool → query que envelheceria a cada tool nova.
            void utils.invalidate();
            break;
          case "navegar":
            // A navegação acontece no FIM do turno: cada página monta o seu
            // DashboardLayout, então trocar de rota agora desmontaria quem
            // hospeda este chat (a página /assistente) no meio do stream.
            if (caminhoPermitido(ev.path)) navegarPara = ev.path;
            break;
          case "confirmacao":
            setEtapa(null);
            atualizarAssistente((m) => ({ ...m, id: ev.messageId, pendencia: ev.pendencia, streaming: false }));
            break;
          case "erro":
            toast.error(ev.mensagem);
            atualizarAssistente((m) => ({ ...m, streaming: false, content: m.content || `⚠️ ${ev.mensagem}` }));
            break;
          case "fim":
            break;
        }
      };

      try {
        const r = await fetch(ASSISTENTE_STREAM_PATH, {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            ...pedido,
            telaAtual: location,
            modulo: moduloRef.current ?? undefined,
            // Lido no momento do envio: é o retrato da tela naquele instante.
            contexto: lerContexto() ?? undefined,
          }),
          signal: controller.signal,
        });
        if (!r.ok || !r.body) {
          const corpo = (await r.json().catch(() => null)) as { erro?: string } | null;
          throw new Error(corpo?.erro ?? `Falha (${r.status})`);
        }
        await lerEventosSse(r.body, tratar);
      } catch (err) {
        if (!controller.signal.aborted) {
          const msg = err instanceof Error ? err.message : "Falha ao falar com o assistente";
          toast.error(msg);
          atualizarAssistente((m) => ({ ...m, streaming: false, content: m.content || `⚠️ ${msg}` }));
        }
      } finally {
        // Se o turno foi abortado por troca de conversa, a lista já é da
        // outra conversa: não mexer nela.
        if (conversaAtualRef.current === conversaDoTurno) {
          atualizarAssistente((m) => ({ ...m, streaming: false, ferramentas: m.ferramentas.map((f) => ({ ...f, emAndamento: false })) }));
        }
        if (abortRef.current === controller) {
          ocupadoRef.current = false;
          setOcupado(false);
          setEtapa(null);
          abortRef.current = null;
        }
        turnoConversaRef.current = null;
        if (conversaDoTurno !== null) {
          await utils.assistente.mensagens.invalidate({ conversationId: conversaDoTurno });
        }
        void utils.assistente.conversas.invalidate();
        if (navegarPara) {
          setLocation(navegarPara);
          onNavegou?.();
        }
      }
    },
    [atualizarAssistente, location, onConversaCriada, onNavegou, setLocation, utils],
  );

  const enviar = useCallback(
    (texto: string, anexos: AnexoUi[] = []) => {
      const limpo = texto.trim();
      if (!limpo && anexos.length === 0) return;
      // A mensagem enviada passa a mostrar o anexo pelo endpoint (o arquivo
      // já está gravado). Guardar a URL local aqui manteria viva uma
      // referência que o composer acabou de revogar.
      const semPreview = anexos.map(({ previewUrl: _, ...resto }) => resto);
      setMensagens((lista) => [
        ...lista,
        { id: `u-${Date.now()}`, role: "user", content: limpo, anexos: semPreview, ferramentas: [] },
        { id: `a-${Date.now()}`, role: "assistant", content: "", ferramentas: [], streaming: true },
      ]);
      void rodar({ conversationId: conversaAtualRef.current ?? undefined, texto: limpo, anexoIds: anexos.map((a) => a.id) });
    },
    [rodar],
  );

  /**
   * Responde sem sair do navegador: nada vai ao servidor e nada é gravado.
   * Serve ao workspace cujo plano não inclui o Assistente — o pedido é aceito
   * e respondido com a vitrine, em vez de o campo ficar morto.
   */
  const responderLocalmente = useCallback((texto: string, resposta: string) => {
    const limpo = texto.trim();
    if (!limpo) return;
    const agora = Date.now();
    setMensagens((lista) => [
      ...lista,
      { id: `u-${agora}`, role: "user", content: limpo, ferramentas: [] },
      { id: `a-${agora}`, role: "assistant", content: resposta, ferramentas: [], upsell: true },
    ]);
  }, []);

  const truncar = trpc.assistente.truncar.useMutation();

  /**
   * Reescreve a conversa a partir de uma mensagem do usuário: apaga dali em
   * diante e manda de novo (com o texto editado, se houver). É o que sustenta
   * "editar" e "refazer" sem empilhar duas versões no histórico do modelo.
   */
  const refazerDe = useCallback(
    async (messageId: number, textoNovo?: string) => {
      if (ocupadoRef.current) return;
      const conversationId = conversaAtualRef.current;
      if (conversationId === null) return;
      const alvo = mensagens.find((m) => m.id === messageId);
      const texto = (textoNovo ?? alvo?.content ?? "").trim();
      const anexos = alvo?.anexos ?? [];
      // Mensagem só de anexo tem texto vazio e ainda assim pode ser reenviada.
      if (!texto && anexos.length === 0) return;
      try {
        await truncar.mutateAsync({
          conversationId,
          apartirDeMessageId: messageId,
          // Os mesmos anexos vão no reenvio: apagá-los aqui mataria o turno.
          manterAnexos: anexos.map((a) => a.id),
        });
      } catch (err) {
        toast.error(toUserFriendlyError(err, "Não consegui refazer daqui"));
        return;
      }
      setMensagens((lista) => {
        const i = lista.findIndex((m) => m.id === messageId);
        const base = i >= 0 ? lista.slice(0, i) : lista;
        return [
          ...base,
          { id: `u-${Date.now()}`, role: "user", content: texto, anexos, ferramentas: [] },
          { id: `a-${Date.now()}`, role: "assistant", content: "", ferramentas: [], streaming: true },
        ];
      });
      void rodar({ conversationId, texto, anexoIds: anexos.map((a) => a.id) });
    },
    [mensagens, rodar, truncar],
  );

  const responderConfirmacao = useCallback(
    (messageId: number, toolCallId: string, aprovado: boolean) => {
      setMensagens((lista) => {
        const semPendencia = lista.map((m) => (m.id === messageId ? { ...m, pendencia: null } : m));
        return [...semPendencia, { id: `a-${Date.now()}`, role: "assistant", content: "", ferramentas: [], streaming: true }];
      });
      void rodar({ conversationId: conversaAtualRef.current ?? undefined, retomar: { messageId, toolCallId, aprovado } });
    },
    [rodar],
  );

  const cancelar = useCallback(() => abortRef.current?.abort(), []);

  return {
    mensagens,
    ocupado,
    etapa,
    enviar,
    responderLocalmente,
    refazerDe,
    responderConfirmacao,
    cancelar,
    modulo,
    setModulo,
    carregando: conversationId !== null && historico.isLoading,
  };
}

export type SessaoAssistente = ReturnType<typeof useAssistente>;
